import { c as _c } from "react/compiler-runtime";
import { ExternalLink, FileText, Link2 } from 'lucide-react';
import Image from 'next/image';
import { ROTULO_DO_TIPO_DE_MIDIA } from '@/shared/constants';
import type { OutboundMediaTipo } from '@/shared/types/midiaDeSaida';
import { usePreviaDaMidia } from '../hooks/usePreviaDaMidia';
import type { SimulacaoLink, SimulacaoReplyOption, SimulacaoMessageBubbleProps } from '../types';
import { dataHoraDoEvento } from '../utils/rotulosDaSimulacao';
const TIPOS_COM_PREVIA = new Set<OutboundMediaTipo>(['IMAGE', 'AUDIO', 'VIDEO']);
const LinkCard = t0 => {
  const $ = _c(9);
  const {
    link
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Link2 className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== link.label) {
    t2 = <span className="min-w-0 flex-1 font-medium break-words">{link.label}</span>;
    $[1] = link.label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== link.label || $[4] !== link.target) {
    t3 = link.target?.safe ? <a href={link.target.href} target="_blank" rel="noopener noreferrer" aria-label={`Abrir ${link.label}`} className="text-brand-700 inline-flex shrink-0 items-center gap-1 font-semibold underline">Abrir<ExternalLink className="h-3 w-3" aria-hidden="true" /></a> : <span className="text-[10px] text-neutral-500">{link.target ? "Link inseguro" : "Indispon\xEDvel"}</span>;
    $[3] = link.label;
    $[4] = link.target;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex items-center gap-2 rounded-[8px] border border-solid border-neutral-200 bg-neutral-50 p-2">{t1}{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
const OptionButton = t0 => {
  const $ = _c(7);
  const {
    option,
    disabled,
    onClick
  } = t0;
  let t1;
  if ($[0] !== option.description) {
    t1 = option.description && <span className="text-[10px] font-normal text-neutral-500">{option.description}</span>;
    $[0] = option.description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== disabled || $[3] !== onClick || $[4] !== option.label || $[5] !== t1) {
    t2 = <button type="button" disabled={disabled} onClick={onClick} className="text-brand-700 flex flex-col rounded-[8px] border border-solid border-neutral-200 bg-white px-3 py-2 text-left text-xs font-medium disabled:opacity-50">{option.label}{t1}</button>;
    $[2] = disabled;
    $[3] = onClick;
    $[4] = option.label;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export const SimulacaoMessageBubble = t0 => {
  const $ = _c(57);
  const {
    message,
    answered,
    interactionEnabled,
    fileUnavailable,
    simulacaoId,
    onReply,
    onOpenFile,
    onFileUnavailable,
    superficie: t1
  } = t0;
  const superficie = t1 === undefined ? "ESCRITORIO" : t1;
  const fileId = message.kind === "file" && !fileUnavailable ? message.file.fileId : null;
  const previewFileId = message.kind === "file" && TIPOS_COM_PREVIA.has(message.file.mediaType) ? fileId : null;
  const preview = usePreviaDaMidia(simulacaoId, previewFileId, onFileUnavailable, superficie);
  const isClient = message.role === "client";
  const interaction = message.kind === "interactive" ? message.interaction : undefined;
  const optionsDisabled = answered || !interactionEnabled;
  let t2;
  if ($[0] !== interaction || $[1] !== onReply) {
    t2 = optionId => {
      if (interaction) {
        onReply({
          messageId: interaction.messageId,
          optionId
        });
      }
    };
    $[0] = interaction;
    $[1] = onReply;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const reply = t2;
  if (message.kind === "session") {
    let t3;
    if ($[3] !== message.author) {
      t3 = <span className="font-semibold">{message.author}</span>;
      $[3] = message.author;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const t4 = message.content;
    const t5 = message.createdAt;
    let t6;
    if ($[5] !== message.createdAt) {
      t6 = dataHoraDoEvento(message.createdAt);
      $[5] = message.createdAt;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== message.createdAt || $[8] !== t6) {
      t7 = <time dateTime={t5}>{t6}</time>;
      $[7] = message.createdAt;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== message.content || $[11] !== t3 || $[12] !== t7) {
      t8 = <div className="self-center rounded-md bg-white/90 px-3 py-1.5 text-center text-[10px] text-neutral-600 shadow-sm">{t3} · {t4} ·{" "}{t7}</div>;
      $[10] = message.content;
      $[11] = t3;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  const t3 = `flex max-w-[88%] flex-col gap-2 rounded-[8px] px-3 py-2 text-xs leading-relaxed break-words text-neutral-800 shadow-sm ${isClient ? "bg-success-100 self-end rounded-br-sm" : "self-start rounded-bl-sm bg-white"}`;
  let t4;
  if ($[14] !== message.author) {
    t4 = <span className="text-[10px] font-semibold text-neutral-600">{message.author}</span>;
    $[14] = message.author;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== message.kind) {
    t5 = message.kind === "template" && <span className="text-brand-700 text-[10px] font-semibold">Template WhatsApp</span>;
    $[16] = message.kind;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== message.content) {
    t6 = message.content && <span className="whitespace-pre-wrap">{message.content}</span>;
    $[18] = message.content;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== fileId || $[21] !== message.file || $[22] !== message.kind || $[23] !== onOpenFile || $[24] !== preview) {
    t7 = message.kind === "file" && <div className="flex flex-col gap-2">{preview.url && message.file.mediaType === "IMAGE" && <Image unoptimized={true} src={preview.url} alt={message.file.name} width={260} height={180} onLoad={preview.registrarCarga} onError={preview.registrarErro} className="h-auto max-h-[180px] w-auto max-w-full rounded-[8px] object-contain" />}{preview.url && message.file.mediaType === "AUDIO" && <audio controls={true} src={preview.url} onLoadedMetadata={preview.registrarCarga} onError={preview.registrarErro} className="w-full max-w-[260px]" />}{preview.url && message.file.mediaType === "VIDEO" && <video controls={true} preload="metadata" src={preview.url} onLoadedMetadata={preview.registrarCarga} onError={preview.registrarErro} className="max-h-[180px] w-full rounded-[8px]" />}<div className="flex items-center gap-2 rounded-[8px] border border-solid border-neutral-200 bg-neutral-50 p-2"><FileText className="h-5 w-5 shrink-0 text-neutral-500" aria-hidden="true" /><div className="flex min-w-0 flex-1 flex-col"><strong className="truncate text-xs">{message.file.name}</strong><span className="text-[10px] text-neutral-500">{ROTULO_DO_TIPO_DE_MIDIA[message.file.mediaType]}</span></div>{fileId ? <button type="button" onClick={() => onOpenFile(fileId)} aria-label={`Baixar arquivo ${message.file.name}`} className="text-brand-700 shrink-0 font-semibold underline">Baixar</button> : <span className="text-[10px] text-neutral-500">Arquivo indisponível</span>}</div></div>;
    $[20] = fileId;
    $[21] = message.file;
    $[22] = message.kind;
    $[23] = onOpenFile;
    $[24] = preview;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== message.id || $[27] !== message.kind || $[28] !== message.links) {
    t8 = (message.kind === "text" || message.kind === "template") && message.links.map((link, index) => <LinkCard key={`${message.id}-link-${index}`} link={link} />);
    $[26] = message.id;
    $[27] = message.kind;
    $[28] = message.links;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== message.kind || $[31] !== message.link) {
    t9 = message.kind === "signature" && <LinkCard link={message.link} />;
    $[30] = message.kind;
    $[31] = message.link;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== answered || $[34] !== interaction || $[35] !== message.id || $[36] !== optionsDisabled || $[37] !== reply) {
    t10 = interaction && <div className="flex flex-col gap-1.5 border-t border-solid border-neutral-200 pt-2">{interaction.kind === "form" ? <form className="flex flex-col gap-2" onSubmit={_temp}>{(interaction.fields ?? []).map(_temp3)}<button type="submit" disabled={true} className="bg-brand-600 rounded-[8px] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">{interaction.submitLabel ?? "Enviar resposta"}</button></form> : interaction.kind === "urlButton" ? interaction.urlButton?.target?.safe ? <a href={interaction.urlButton.target.href} target="_blank" rel="noopener noreferrer" className="text-brand-700 rounded-[8px] border border-solid border-neutral-200 bg-white px-3 py-2 text-center text-xs font-medium">{interaction.urlButton.label}</a> : <span className="text-[10px] text-neutral-500">{interaction.urlButton?.target ? "Link inseguro" : "Link indispon\xEDvel"}</span> : interaction.kind === "list" && interaction.sections?.length ? interaction.sections.map((section, index_0) => <div key={`${message.id}-section-${index_0}`} className="flex flex-col gap-1">{section.title && <span className="text-[10px] font-semibold text-neutral-500">{section.title}</span>}{section.options.map(option_0 => <OptionButton key={option_0.id} option={option_0} disabled={optionsDisabled} onClick={() => reply(option_0.id)} />)}</div>) : (interaction.options ?? []).map(option_1 => <OptionButton key={option_1.id} option={option_1} disabled={optionsDisabled} onClick={() => reply(option_1.id)} />)}{answered && <span className="text-[10px] text-neutral-500">Resposta enviada</span>}</div>;
    $[33] = answered;
    $[34] = interaction;
    $[35] = message.id;
    $[36] = optionsDisabled;
    $[37] = reply;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const t11 = message.createdAt;
  let t12;
  if ($[39] !== message.createdAt) {
    t12 = dataHoraDoEvento(message.createdAt);
    $[39] = message.createdAt;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== message.createdAt || $[42] !== t12) {
    t13 = <time dateTime={t11}>{t12}</time>;
    $[41] = message.createdAt;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const t14 = message.status && ` · ${message.status}`;
  let t15;
  if ($[44] !== t13 || $[45] !== t14) {
    t15 = <span className="self-end text-[10px] text-neutral-500">{t13}{t14}</span>;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t10 || $[48] !== t15 || $[49] !== t3 || $[50] !== t4 || $[51] !== t5 || $[52] !== t6 || $[53] !== t7 || $[54] !== t8 || $[55] !== t9) {
    t16 = <div className={t3}>{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t15}</div>;
    $[47] = t10;
    $[48] = t15;
    $[49] = t3;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
    $[53] = t7;
    $[54] = t8;
    $[55] = t9;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
};
function _temp(event) {
  return event.preventDefault();
}
function _temp2(option) {
  return <option key={option.id} value={option.id}>{option.label}</option>;
}
function _temp3(field) {
  return <label key={field.id} className="flex flex-col gap-1 text-[11px] font-medium">{field.label}{field.type === "select" ? <select required={field.required} disabled={true} defaultValue="" className="h-9 rounded-[8px] border border-solid border-neutral-200 bg-white px-2 text-xs"><option value="">Selecione</option>{(field.options ?? []).map(_temp2)}</select> : <input type={field.type} required={field.required} disabled={true} className="h-9 rounded-[8px] border border-solid border-neutral-200 bg-white px-2 text-xs" />}</label>;
}